// Dead letter service — API_INTEGRATION.md §3.28
// Platform-level DLQ management. Traceability: FR-BACK-009, FR-BACK-010

import { apiClient, apiClientPaginated } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type DeadLetterStatus = 'PENDING' | 'REPLAYED' | 'DISCARDED';

export interface DeadLetter {
  id: string;
  exchange: string;
  routing_key: string;
  /** Only on GET /dead-letters/:id — the list omits it. */
  payload?: Record<string, unknown>;
  error_message: string;
  original_queue: string;
  retry_count: number;
  status: DeadLetterStatus;
  replayed_at: string | null;
  created_at: string;
}

export interface DeadLetterStats {
  total: number;
  by_exchange: Record<string, number>;
  by_hour: Array<{ hour: string; count: number }>;
}

export interface DeadLetterRetryResponse {
  id: string;
  status: 'REPLAYED';
  replayed_by: string;
  replayed_at: string;
  new_message_id: string;
}

export interface DeadLetterListParams {
  page?: number;
  limit?: number;
  exchange?: string;
  status?: DeadLetterStatus;
  /** YYYY-MM-DD, inclusive. */
  start_date?: string;
  end_date?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service ────────────────────────────────────────────────────────────────

export const deadLetterService = {
  async list(params: DeadLetterListParams = {}): Promise<{
    data: DeadLetter[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const q = buildQuery({
      page: params.page,
      limit: params.limit ?? 20,
      exchange: params.exchange,
      status: params.status,
      start_date: params.start_date,
      end_date: params.end_date,
    });
    return apiClientPaginated<DeadLetter[]>(`/dead-letters${q}`);
  },

  async getById(id: string): Promise<DeadLetter> {
    return apiClient<DeadLetter>(`/dead-letters/${id}`);
  },

  async retry(id: string): Promise<DeadLetterRetryResponse> {
    return apiClient<DeadLetterRetryResponse>(`/dead-letters/${id}/retry`, { method: 'POST' });
  },

  async discard(id: string): Promise<void> {
    return apiClient<void>(`/dead-letters/${id}`, { method: 'DELETE' });
  },

  async getStats(): Promise<DeadLetterStats> {
    return apiClient<DeadLetterStats>('/dead-letters/stats');
  },
};
